import { useI18n } from 'vue-i18n'

/* Assets and Helpers */
import { amountFormat } from '@/assets/scripts/amounts'
import { getDate } from '@/assets/scripts/dateBuilders'
import { ACCOUNT_NUMBER } from '@/assets/constants/masks'

export const parseToSelectCardMobile = (textBlocks, cardSelected) => {
	return textBlocks.map(textBlock => {
		return { ...textBlock, selected: textBlock.id === cardSelected }
	})
}

/* Parser for savings > chargeDP > Step1 */
export const selectedProductInfoParser = (selectedProduct, currency) => {
	const { t } = useI18n()

	return [
		{
			label: t('savings.mySavings.charge.step1.productNameLabel'),
			content: selectedProduct.termAccountProduct.name
		}, {
			label: t('savings.mySavings.charge.step1.productDescriptionLabel'),
			content: selectedProduct.termAccountProduct.shortDescription
		}, {
			label: t('savings.mySavings.charge.step1.productDurationLabel'),
			content: t('savings.mySavings.charge.step1.productDurationContent', { numberOfDays: selectedProduct.numberOfDays, ageInDays: selectedProduct.ageInDays })
		}, {
			label: t('savings.mySavings.charge.step1.productMinAmountLabel'),
			content: amountFormat(selectedProduct.termAccountProduct.minimumAmount, currency)
		}, {
			label: t('savings.mySavings.charge.step1.productMaxAmountLabel'),
			content: amountFormat(selectedProduct.termAccountProduct.maximumAmount, currency)
		}, {
			label: t('savings.mySavings.charge.step1.productAmountAppliedLabel'),
			content: amountFormat(selectedProduct.balance, currency)
		}, {
			label: t('savings.mySavings.charge.step1.productTanbLabel'),
			content: `${selectedProduct.interestRate}%`
		}, {
			label: t('savings.mySavings.charge.step1.productCreationDateLabel'),
			content: getDate(selectedProduct.creationDate),
			classes: 'capitalize'
		}, {
			label: t('savings.mySavings.charge.step1.productMaturityDateLabel'),
			content: getDate(selectedProduct.maturityDate),
			classes: 'capitalize'
		}
	]
}

/* Parser for savings > chargeDP > Step2 */
export const overviewOfSelectedInfoParser = (selectedProduct, currency, accountsBalances, formData, balance) => {
	const { t } = useI18n()

	return [
        /* Deposit Information */
        {
            label: t('savings.mySavings.charge.step2.depositInformationTitle'),
            icon: 'info',
            lines: [{
                label: t('savings.mySavings.charge.step2.accountLabel'),
                content: selectedProduct.accountNumber,
                mask: ACCOUNT_NUMBER
            }, {
                label: t('savings.mySavings.charge.step2.productNameLabel'),
                content: selectedProduct.termAccountProduct.name
            }, {
                label: t('savings.mySavings.charge.step2.productDescriptionLabel'),
                content: selectedProduct.termAccountProduct.shortDescription
            }, {
                label: t('savings.mySavings.charge.step2.productDurationLabel'),
                content: t('savings.mySavings.charge.step2.productDurationContent', { numberOfDays: selectedProduct.numberOfDays })
            }, {
                label: t('savings.mySavings.charge.step2.productMinAmountLabel'),
                content: amountFormat(selectedProduct.termAccountProduct.minimumAmount, currency)
            }, {
                label: t('savings.mySavings.charge.step2.productMaxAmountLabel'),
                content: amountFormat(selectedProduct.termAccountProduct.maximumAmount, currency)
            }, {
                label: t('savings.mySavings.charge.step2.productAmountAppliedLabel'),
                content: balance ? amountFormat(balance.data.balance, currency) : amountFormat(selectedProduct.balance, currency)
            }, {
                label: t('savings.mySavings.charge.step2.productTanbLabel'),
                content: `${selectedProduct.interestRate}%`
            }, {
                label: t('savings.mySavings.charge.step2.productCreationDateLabel'),
                content: getDate(selectedProduct.creationDate),
                classes: 'capitalize'
            }, {
                label: t('savings.mySavings.charge.step2.productMaturityDateLabel'),
                content: getDate(selectedProduct.maturityDate),
                classes: 'capitalize'
            }]
        },
        /* Source Account */
        {
            label: t('savings.mySavings.charge.step2.sourceAccountTitle'),
            icon: 'account-balance',
            lines: [{
                label: t('savings.mySavings.charge.step2.sourceAccountLabel'),
                content: accountsBalances.find(account => account.id === formData.originAccountId).accountNumber,
                mask: ACCOUNT_NUMBER
            }]
        },
        /* Amount */
        {
            label: t('savings.mySavings.charge.step2.amountTitle'),
            icon: 'bills',
            lines: [{
                label: t('savings.mySavings.charge.step2.amountLabel'),
                content: amountFormat(formData.amount, currency)
            }]
        }
    ]
}

/* Parser for savings > chargeDP > Step3 */
export const overviewOfSelectedInfoParserStep3 = (selectedProduct, currency, accountsBalances, formData, responseData) => {
	const { t } = useI18n()

	return [
        /* Deposit Information */
        {
            label: t('savings.mySavings.charge.step3.depositInformationTitle'),
            icon: 'coin-drop',
            lines: [{
                label: t('savings.mySavings.charge.step3.accountLabel'),
                content: selectedProduct.accountNumber,
                mask: ACCOUNT_NUMBER
            }, {
                label: t('savings.mySavings.charge.step3.productNameLabel'),
                content: selectedProduct.termAccountProduct.name
            }, {
                label: t('savings.mySavings.charge.step3.productDescriptionLabel'),
                content: selectedProduct.termAccountProduct.shortDescription
            }, {
                label: t('savings.mySavings.charge.step3.productDurationLabel'),
                content: t('savings.mySavings.charge.step3.productDurationContent', { numberOfDays: selectedProduct.numberOfDays })
            }, {
                label: t('savings.mySavings.charge.step3.productMinAmountLabel'),
                content: amountFormat(selectedProduct.termAccountProduct.minimumAmount, currency)
            }, {
                label: t('savings.mySavings.charge.step3.productMaxAmountLabel'),
                content: amountFormat(selectedProduct.termAccountProduct.maximumAmount, currency)
            }, {
                label: t('savings.mySavings.charge.step3.productAmountAppliedLabel'),
                content: responseData.balance ? amountFormat(responseData.balance, currency) : amountFormat(selectedProduct.balance, currency)
            }, {
                label: t('savings.mySavings.charge.step3.productTanbLabel'),
                content: `${selectedProduct.interestRate}%`
            }, {
                label: t('savings.mySavings.charge.step3.productCreationDateLabel'),
                content: getDate(selectedProduct.creationDate),
                classes: 'capitalize'
            }, {
                label: t('savings.mySavings.charge.step3.productMaturityDateLabel'),
                content: getDate(selectedProduct.maturityDate),
                classes: 'capitalize'
            }]
        },
        /* Source Account */
        {
            label: t('savings.mySavings.charge.step3.sourceAccountTitle'),
            icon: 'account-balance',
            lines: [{
                label: t('savings.mySavings.charge.step3.sourceAccountLabel'),
                content: accountsBalances.find(account => account.id === formData.originAccountId).accountNumber,
                mask: ACCOUNT_NUMBER
            }]
        },
        /* Amount */
        {
            label: t('savings.mySavings.charge.step3.amountTitle'),
            icon: 'bills',
            lines: [{
                label: t('savings.mySavings.charge.step3.amountLabel'),
                content: amountFormat(formData.amount, currency)
            }]
        }
    ]
}
